import {
  Avatar,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColorModeToggle, LoadingButton } from "src/grading/components";
import KeyValueCard from "src/grading/components/KeyValueCard/KeyValueCard";
import PageHeader from "src/grading/layout/PageHeader";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  useGetUserQuery,
  useUpdateProfileImageMutation,
  useUpdateProfileMutation,
} from "src/store/reducers/auth/authApiSlice";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import { handleApiError, handleApiResponse } from "src/utils/api";
import * as Yup from "yup";
import PasswordChangeModal from "./PasswordChangeModal/PasswordChangeModal";

/** Staff profile: name, display name and photo. No body measurements or branch of service. */
const Profile = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const profile = useAppSelector(selectCurrentUser);
  useGetUserQuery();
  const [updateProfile, { isLoading: isSaving }] = useUpdateProfileMutation();
  const [updateProfileImage, { isLoading: isUploading }] =
    useUpdateProfileImageMutation();
  const [passwordOpen, setPasswordOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: profile?.firstName ?? "",
      lastName: profile?.lastName ?? "",
      displayName: profile?.displayName ?? "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required(
        t("invite.firstNameRequired", {
          defaultValue: "First name is required",
        }),
      ),
      lastName: Yup.string().required(
        t("invite.lastNameRequired", { defaultValue: "Last name is required" }),
      ),
      displayName: Yup.string().max(
        60,
        t("profile.displayNameMax", {
          defaultValue: "Keep it under 60 characters",
        }),
      ),
    }),
    onSubmit: async (values) => {
      try {
        const response = await updateProfile(values).unwrap();
        handleApiResponse(dispatch, response);
      } catch (error) {
        handleApiError(dispatch, error);
      }
    },
  });

  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  const onPhoto = async (file?: File) => {
    if (!file) return;
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await updateProfileImage(formData).unwrap();
      handleApiResponse(dispatch, response);
    } catch (error) {
      handleApiError(dispatch, error);
    }
  };

  if (!profile) return null;
  const { values, errors, touched, handleChange, handleSubmit, dirty } = formik;

  return (
    <Box sx={{ maxWidth: 640 }}>
      <PageHeader title={t("profile.title", { defaultValue: "Profile" })} />
      <Stack spacing={3}>
        <KeyValueCard
          title={t("profile.account", { defaultValue: "Account" })}
          rows={[
            {
              label: t("profile.name", { defaultValue: "Name" }),
              value:
                profile.displayName ||
                `${profile.firstName} ${profile.lastName}`,
            },
            {
              label: t("profile.email", { defaultValue: "Email" }),
              value: profile.email,
            },
            ...(profile.organization
              ? [
                  {
                    label: t("profile.organization", {
                      defaultValue: "Organization",
                    }),
                    value: profile.organization.name,
                  },
                ]
              : []),
          ]}
        />
        <Paper sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Avatar
              src={profile.profileImageUrl}
              alt=""
              sx={{ width: 72, height: 72 }}
            >
              {profile.firstName?.[0]}
              {profile.lastName?.[0]}
            </Avatar>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => onPhoto(e.target.files?.[0])}
            />
            <Button
              variant="outlined"
              sx={{ ml: "auto" }}
              disabled={isUploading}
              onClick={() => fileInput.current?.click()}
            >
              {isUploading
                ? t("profile.uploading", { defaultValue: "Uploading…" })
                : t("profile.changePhoto", { defaultValue: "Change photo" })}
            </Button>
          </Stack>
          <form onSubmit={handleSubmit} noValidate>
            <Stack spacing={2}>
              <TextField
                name="firstName"
                label={t("profile.firstName", { defaultValue: "First name" })}
                value={values.firstName}
                onChange={handleChange}
                error={touched.firstName && !!errors.firstName}
                helperText={touched.firstName && errors.firstName}
                fullWidth
              />
              <TextField
                name="lastName"
                label={t("profile.lastName", { defaultValue: "Last name" })}
                value={values.lastName}
                onChange={handleChange}
                error={touched.lastName && !!errors.lastName}
                helperText={touched.lastName && errors.lastName}
                fullWidth
              />
              <TextField
                name="displayName"
                label={t("profile.displayName", {
                  defaultValue:
                    "Display name (shown to volunteers and students)",
                })}
                value={values.displayName}
                onChange={handleChange}
                error={touched.displayName && !!errors.displayName}
                helperText={touched.displayName && errors.displayName}
                fullWidth
              />
              <Stack direction="row" spacing={2}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  label={t("profile.save", { defaultValue: "Save" })}
                  loading={isSaving}
                  disabled={!dirty}
                />
                <Button variant="text" onClick={() => setPasswordOpen(true)}>
                  {t("profile.changePassword", {
                    defaultValue: "Change password",
                  })}
                </Button>
              </Stack>
            </Stack>
          </form>
        </Paper>
        <Paper
          component="section"
          aria-labelledby="profile-appearance"
          sx={{ p: 3 }}
        >
          <Typography
            id="profile-appearance"
            variant="h4"
            component="h2"
            sx={{ mb: 2 }}
          >
            {t("theme.appearance")}
          </Typography>
          <ColorModeToggle variant="segmented" />
        </Paper>
      </Stack>
      <PasswordChangeModal
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        title={t("profile.changePassword", { defaultValue: "Change password" })}
        message={t("profile.changePasswordBody", {
          defaultValue: "Enter your current password and a new one.",
        })}
        confirmationButtonTitle={t("profile.updatePassword", {
          defaultValue: "Update password",
        })}
      />
    </Box>
  );
};

export default Profile;
